/** Adresse de facturation + livraison. Case « identique » = copie ; sinon vide. */
import { useEffect, useState } from "react";
import { CheckField } from "../../components/CheckField";
import { Field } from "../../components/Field";
import { COUNTRIES, type ClientDraft } from "./types";

type AddressSectionProps = {
  draft: ClientDraft;
  setDraft: (next: ClientDraft) => void;
  tried?: boolean;
};

type ShipPatch = Pick<
  ClientDraft,
  | "ship_address_line"
  | "ship_address_line2"
  | "ship_city"
  | "ship_region"
  | "ship_postal_code"
  | "ship_country_code"
>;

function CountrySelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (code: string) => void;
}) {
  return (
    <label className="fx-field flex flex-col gap-1 text-xs">
      Pays
      <select
        className="rounded-md border px-2.5 py-2 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {value === "" ? <option value="">—</option> : null}
        {COUNTRIES.map((c) => (
          <option key={c.code} value={c.code}>
            {c.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function emptyShip(): ShipPatch {
  return {
    ship_address_line: "",
    ship_address_line2: "",
    ship_city: "",
    ship_region: "",
    ship_postal_code: "",
    ship_country_code: "",
  };
}

function shipFromBill(d: ClientDraft): ShipPatch {
  return {
    ship_address_line: d.address_line,
    ship_address_line2: d.address_line2,
    ship_city: d.city,
    ship_region: d.region,
    ship_postal_code: d.postal_code,
    ship_country_code: d.country_code,
  };
}

function shipIsEmpty(d: ClientDraft): boolean {
  return (
    d.ship_address_line === "" &&
    d.ship_address_line2 === "" &&
    d.ship_city === "" &&
    d.ship_region === "" &&
    d.ship_postal_code === "" &&
    d.ship_country_code === ""
  );
}

function sameAsBilling(d: ClientDraft): boolean {
  if (shipIsEmpty(d)) return false;
  const s = shipFromBill(d);
  return (
    d.ship_address_line === s.ship_address_line &&
    d.ship_address_line2 === s.ship_address_line2 &&
    d.ship_city === s.ship_city &&
    d.ship_region === s.ship_region &&
    d.ship_postal_code === s.ship_postal_code &&
    d.ship_country_code === s.ship_country_code
  );
}

function shipEquals(d: ClientDraft, s: ShipPatch): boolean {
  return (
    d.ship_address_line === s.ship_address_line &&
    d.ship_address_line2 === s.ship_address_line2 &&
    d.ship_city === s.ship_city &&
    d.ship_region === s.ship_region &&
    d.ship_postal_code === s.ship_postal_code &&
    d.ship_country_code === s.ship_country_code
  );
}

export function AddressSection({ draft, setDraft, tried }: AddressSectionProps) {
  const [same, setSame] = useState(() => sameAsBilling(draft));

  useEffect(() => {
    setSame(sameAsBilling(draft));
  }, [draft.id]);

  useEffect(() => {
    if (!same) return;
    const next = shipFromBill(draft);
    if (!shipEquals(draft, next)) setDraft({ ...draft, ...next });
  }, [
    same,
    draft.address_line,
    draft.address_line2,
    draft.city,
    draft.region,
    draft.postal_code,
    draft.country_code,
  ]);

  function toggleSame(checked: boolean) {
    setSame(checked);
    setDraft(checked ? { ...draft, ...shipFromBill(draft) } : { ...draft, ...emptyShip() });
  }

  return (
    <section className="grid gap-4">
      <h2 className="m-0 font-[Nunito] text-lg text-indigo-950">Adresse</h2>
      <div className="grid gap-3">
        <h3 className="m-0 text-sm font-medium text-indigo-800">Adresse de facturation</h3>
        <div className="grid grid-cols-2 gap-3 max-[640px]:grid-cols-1">
          <Field
            label="Rue"
            required
            showMiss={Boolean(tried && !draft.address_line.trim())}
            missText="La rue est obligatoire pour enregistrer."
            value={draft.address_line}
            onChange={(e) => setDraft({ ...draft, address_line: e.target.value })}
          />
          <Field label="Appt / Bâtiment" value={draft.address_line2} onChange={(e) => setDraft({ ...draft, address_line2: e.target.value })} />
          <Field
            label="Ville"
            required
            showMiss={Boolean(tried && !draft.city.trim())}
            missText="La ville est obligatoire pour enregistrer."
            value={draft.city}
            onChange={(e) => setDraft({ ...draft, city: e.target.value })}
          />
          <Field label="Région / Département" value={draft.region} onChange={(e) => setDraft({ ...draft, region: e.target.value })} />
          <Field
            label="Code postal"
            required
            showMiss={Boolean(tried && !draft.postal_code.trim())}
            missText="Le code postal est obligatoire pour enregistrer."
            value={draft.postal_code}
            onChange={(e) => setDraft({ ...draft, postal_code: e.target.value })}
          />
          <CountrySelect value={draft.country_code} onChange={(country_code) => setDraft({ ...draft, country_code })} />
        </div>
      </div>
      <div className="grid gap-3">
        <h3 className="m-0 text-sm font-medium text-indigo-800">Adresse de livraison</h3>
        <CheckField label="Identique à l’adresse de facturation" checked={same} onChange={toggleSame} />
        {same ? (
          <p className="m-0 text-sm text-slate-500">Livraison au même endroit que la facturation.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 max-[640px]:grid-cols-1">
            <Field label="Rue" value={draft.ship_address_line} onChange={(e) => setDraft({ ...draft, ship_address_line: e.target.value })} />
            <Field label="Appt / Bâtiment" value={draft.ship_address_line2} onChange={(e) => setDraft({ ...draft, ship_address_line2: e.target.value })} />
            <Field label="Ville" value={draft.ship_city} onChange={(e) => setDraft({ ...draft, ship_city: e.target.value })} />
            <Field label="Région / Département" value={draft.ship_region} onChange={(e) => setDraft({ ...draft, ship_region: e.target.value })} />
            <Field label="Code postal" value={draft.ship_postal_code} onChange={(e) => setDraft({ ...draft, ship_postal_code: e.target.value })} />
            <CountrySelect value={draft.ship_country_code} onChange={(ship_country_code) => setDraft({ ...draft, ship_country_code })} />
          </div>
        )}
      </div>
    </section>
  );
}
